import React from 'react';
import Box from '@mui/material/Box';
import { Alert } from '@mui/material';
import { Button } from '@material-ui/core';
import LinearProgress from '@material-ui/core/LinearProgress';

import { Modal, ModalContents, ModalTitle } from 'components/Modal/Modal';
import { BackButton } from 'retired/BackButtonLayout/BackButton';
import {
  useMigratedAndToBeMigratedCommunitiesQuery,
  useMigrateErGsToCommunitiesMutation,
} from 'generated/graphql';

export const MigrateERGsToCommunitiesModal = ({ onClickClose }: { onClickClose: () => void }) => {
  const {
    loading: queryMigrationProjectionLoading,
    data: communitiesMigrationProjectionData,
    error: queryMigrationProjectionError,
  } = useMigratedAndToBeMigratedCommunitiesQuery();

  const [
    mutationMigrateERGsToCommunities,
    { data: migrationResultData, loading: migrationMutationLoading, error: migrationMutationError },
  ] = useMigrateErGsToCommunitiesMutation();

  const migratedCommunities =
    communitiesMigrationProjectionData?.migratedAndToBeMigratedCommunities.migratedCommunities;
  const toBeMigratedCommunities =
    communitiesMigrationProjectionData?.migratedAndToBeMigratedCommunities.toBeMigratedCommunities;

  const migrationResultCreatedCommunities =
    migrationResultData?.migrateERGsToCommunities.newlyCreatedCommunities;
  const migrationResultUpdatedCommunities =
    migrationResultData?.migrateERGsToCommunities.updatedCommunities;

  const getActionsSection = (): JSX.Element | undefined => {
    if (queryMigrationProjectionLoading) {
      return undefined;
    }

    if (queryMigrationProjectionError || migrationMutationError) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Button variant="outlined" onClick={onClickClose}>
            Ok
          </Button>
        </div>
      );
    } else if (communitiesMigrationProjectionData && migrationResultData) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Button variant="outlined" onClick={onClickClose}>
            Ok
          </Button>
        </div>
      );
    } else {
      return (
        <div style={{ display: 'flex', gap: 8 }}>
          <Button
            variant="outlined"
            onClick={(): void => {
              onClickClose();
            }}
            disabled={migrationMutationLoading}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={(): void => {
              mutationMigrateERGsToCommunities();
            }}
            disabled={migrationMutationLoading}
          >
            Migrate
          </Button>
        </div>
      );
    }
  };

  return (
    <Modal open fullWidth onClose={onClickClose}>
      <ModalContents
        onClickClose={onClickClose}
        body={
          <div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <BackButton onClick={onClickClose} />
              <ModalTitle style={{ marginBottom: 0, marginLeft: 12 }}>
                Migrate ERGs To Communities
              </ModalTitle>
            </div>
            {(queryMigrationProjectionLoading || migrationMutationLoading) && (
              <div style={{ paddingTop: 16 }}>
                <LinearProgress />
              </div>
            )}

            {queryMigrationProjectionError || migrationMutationError ? (
              <div style={{ marginTop: 16 }}>
                <Alert severity="error">Something went wrong, please try again.</Alert>
              </div>
            ) : (
              !queryMigrationProjectionLoading &&
              communitiesMigrationProjectionData && (
                <Box paddingTop={4}>
                  {migrationResultData ? (
                    <>
                      <div style={{ marginBottom: 16 }}>
                        Successfully created{' '}
                        <strong>{migrationResultCreatedCommunities.length}</strong> and updated{' '}
                        <strong>{migrationResultUpdatedCommunities.length}</strong> communities!
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <div style={{ marginBottom: 8 }}>This action will:</div>
                        {toBeMigratedCommunities?.length ? (
                          <>
                            <div>
                              Create <strong>{toBeMigratedCommunities.length}</strong> communities
                            </div>

                            <ul style={{ maxHeight: 60, overflowY: 'auto' }}>
                              {toBeMigratedCommunities?.map((community) => (
                                <li key={community.id}>{community.title}</li>
                              ))}
                            </ul>
                          </>
                        ) : null}

                        {migratedCommunities?.length ? (
                          <>
                            <div>
                              Update <strong>{migratedCommunities.length}</strong> existing
                              communities
                            </div>

                            <ul style={{ maxHeight: 60, overflowY: 'auto' }}>
                              {migratedCommunities?.map((community) => (
                                <li key={community.id}>{community.title}</li>
                              ))}
                            </ul>
                          </>
                        ) : null}
                      </div>
                      <Box mb={2}>Do you want to proceed?</Box>
                    </>
                  )}
                </Box>
              )
            )}
          </div>
        }
        actions={getActionsSection()}
      />
    </Modal>
  );
};
